import { useEffect, useState } from 'react';
import { Banner, Button, Card, Page, Text } from '@shopify/polaris';
import { MetricCard } from '../components/MetricCard';
import { presentSettingsValue } from '../lib/settingsPresentation';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('uk-UA');
}

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  paid: 'Оплачено',
  pending: 'Очікує оплату',
  unpaid: 'Не оплачено',
  refunded: 'Повернено',
  partially_refunded: 'Частково повернено',
  failed: 'Помилка оплати',
  authorized: 'Авторизовано',
};

const QUEUE_STATUS_LABELS: Record<string, string> = {
  pending: 'Очікує передачі',
  queued: 'У черзі',
  processing: 'Передається',
  running: 'Передається',
  exported: 'Передано',
  completed: 'Передано',
  failed: 'Помилка',
  skipped: 'Пропущено',
};

const EXPORT_STATUS_LABELS: Record<string, string> = {
  pending: 'Очікує передачі',
  queued: 'У черзі',
  processing: 'Передається',
  running: 'Передається',
  exported: 'Передано в 1С',
  completed: 'Передано в 1С',
  failed: 'Помилка передачі',
  skipped: 'Пропущено',
};

function presentStatus(value: unknown, labels: Record<string, string>) {
  if (value === null || value === undefined || value === '') return '—';
  const key = String(value);
  return labels[key] || presentSettingsValue(key);
}

function presentOrderError(value?: string | null) {
  if (!value) return '—';
  if (/ECONNREFUSED|ECONNRESET|ETIMEDOUT|fetch failed|Unable to reach/u.test(value)) {
    return 'Сервіс 1С або синхронізації тимчасово недоступний.';
  }
  if (/timeout|aborted|AbortError/u.test(value)) {
    return 'Передача тривала занадто довго і була зупинена.';
  }
  if (/ONEC_|LIQPAY_|SHOPIFY_|PM2|worker|queue|stack|TypeError|SyntaxError/u.test(value)) {
    return 'Потрібна технічна перевірка цього замовлення.';
  }
  return value;
}

export function OrdersPaymentsPage({ api, onRefreshOverview, readOnly = false }: any) {
  const [report, setReport] = useState<any>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = async () => {
    try {
      const nextReport = await api.getOrdersPayments();
      setReport(nextReport);
      setError('');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося зібрати orders/payments report');
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const runQueuedExport = async () => {
    setBusy(true);
    setMessage('');
    try {
      await api.runQueuedExport();
      setMessage('Чергу замовлень передано на обробку.');
      await refresh();
      await onRefreshOverview();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Експорт черги не вдався');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title="Замовлення й оплати" subtitle="Що оплатилось, що в черзі і що доїхало в 1С">
      <div className="admin-stack">
        {error ? <Banner tone="critical" title="Звіт по замовленнях і оплатах недоступний"><p>{presentOrderError(error)}</p></Banner> : null}
        {message ? <Banner tone="success" title="Дію виконано"><p>{message}</p></Banner> : null}
        {readOnly ? (
          <Banner tone="info" title="Роль перегляду">
            <p>Експорт черги в 1С вимкнений для ролі “Перегляд”. Ця роль бачить статус, але не штовхає замовлення.</p>
          </Banner>
        ) : null}

        <div className="admin-grid admin-grid--metrics">
          <MetricCard label="Режим LiqPay" value={presentSettingsValue(report?.summary?.liqpayMode)} summary="Поточний режим оплат у магазині" status={report?.summary?.liqpayMode === 'live' ? 'live' : 'sandbox'} />
          <MetricCard label="Оплачені" value={String(report?.summary?.paidCount ?? 0)} summary="У вибірці останніх замовлень" status="ok" />
          <MetricCard label="У черзі" value={String(report?.summary?.queuedCount ?? 0)} summary="Очікують передачі в 1С" status={report?.summary?.queuedCount ? 'warning' : 'ok'} />
          <MetricCard label="Передано" value={String(report?.summary?.exportedCount ?? 0)} summary="Останні замовлення, які вже дійшли до 1С" status="ok" />
        </div>

        <Card>
          <div className="admin-action-card">
            <div className="admin-section-head">
              <Text as="h2" variant="headingMd">Ручна дія</Text>
              <Button variant="primary" onClick={runQueuedExport} loading={busy} disabled={readOnly}>Передати замовлення з черги</Button>
            </div>
            <Text as="p" variant="bodySm" tone="subdued">
              Ця дія не змінює каталог. Вона просить систему ще раз передати в 1С замовлення, які вже чекають у черзі.
            </Text>
          </div>
        </Card>

        <Card>
          <div className="admin-stack">
            <Text as="h2" variant="headingMd">Останні релевантні замовлення</Text>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Замовлення</th>
                    <th>Сума</th>
                    <th>Оплата</th>
                    <th>LiqPay</th>
                    <th>Черга</th>
                    <th>Передача в 1С</th>
                    <th>Оновлено</th>
                    <th>Помилка</th>
                  </tr>
                </thead>
                <tbody>
                  {(report?.rows || []).map((row: any) => (
                    <tr key={row.shopifyOrderId}>
                      <td>{row.orderName || row.shopifyOrderId}</td>
                      <td>{row.amount ? `${row.amount} ${row.currency}` : '—'}</td>
                      <td>{presentStatus(row.paymentStatus, PAYMENT_STATUS_LABELS)}</td>
                      <td>{presentStatus(row.liqpayStatus, PAYMENT_STATUS_LABELS)}</td>
                      <td>{presentStatus(row.queueStatus, QUEUE_STATUS_LABELS)}</td>
                      <td>{presentStatus(row.onecExportStatus, EXPORT_STATUS_LABELS)}</td>
                      <td>{formatDateTime(row.updatedAt)}</td>
                      <td>{presentOrderError(row.lastError)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
